import PropTypes from 'prop-types';
import React from 'react';
import _ from 'lodash';
import classnames from 'classnames';
import { Set, Map } from 'immutable';

import FeatureFlagStore from '../../stores/FeatureFlagStore';
import Search from '../../records/Search';
import SeasonSearch from '../../records/SeasonSearch';
import { translate as t } from '../../lib/I18n';
import { StationTypes, SeasonStationTypes, SmartcardStationTypes, SmartcardSeasonStationTypes, BreakPoints, Tutorials } from '../../constants/AppConstants';
import PromoCodeStore from '../../stores/PromoCodeStore';
import connectToStores from '../../helpers/connectToStores';
import PromoCode from '../../records/PromoCode';
import StationFinderActions from '../../actions/StationFinderActions';
import StationFinderStore from '../../stores/StationFinderStore';

import ResponsiveRender from '../../components/ResponsiveRender';
import Icon from '../general/Icon';
import SectionErrors from './SectionErrors';
import StationSelector from './StationSelector';
import ViaAvoid from './ViaAvoid';
import StationFinder from '../stationFinder';
import StationFinderToggle from '../stationFinder/StationFinderToggle';
import BaseModal from '../general/BaseModal';
import Tutorial from '../general/Tutorial';
import TrackingActions from "../../actions/TrackingActions";

class JourneySelectorOpen extends React.PureComponent {
    state = {
        showStationFinderModal: false,
        stationFieldsTouched: false,
        errors: this._getErrors(this.props)
    }

    static propTypes = {
        season: PropTypes.bool,
        search: PropTypes.oneOfType([
            PropTypes.instanceOf(Search),
            PropTypes.instanceOf(SeasonSearch)
        ]).isRequired,
        searchAttempted: PropTypes.bool,
        onChange: PropTypes.func,
        promoCode: PropTypes.instanceOf(PromoCode),
        searchErrors: PropTypes.instanceOf(Set),
        enableStationFinder: PropTypes.bool,
        enableVia: PropTypes.bool,
        overlayResults: PropTypes.bool
    };

    static defaultProps = {
        season: false,
        onChange: _.noop,
        promoCode: null,
        searchErrors: new Set(),
        enableStationFinder: false,
        enableVia: true,
        overlayResults: false,
        searchAttempted: false
    };

    UNSAFE_componentWillReceiveProps(nextProps) {
        this.setState({
            errors: this._getErrors(nextProps)
        });
    }

    _getErrors({ search, searchAttempted, searchErrors }) {
        const stationFieldsTouched = this.state && this.state.stationFieldsTouched;

        let errors = new Map();
        if (searchErrors.has('validateStationsNotEqual')) {
            errors = errors.set('origin', 'booking.journey.errors.sameStation')
                .set('destination', 'booking.journey.errors.sameStation');
        }

        if ((searchAttempted || stationFieldsTouched) && searchErrors.has('validateStationsComplete')) {
            if (!search.origin.nlc) { errors = errors.set('origin', 'booking.journey.errors.missingOrigin'); }
            if (!search.destination.nlc) { errors = errors.set('destination', 'booking.journey.errors.missingDestination'); }
        }

        if (FeatureFlagStore.get('enableViaAvoid') && !this.props.promoCode && !this.props.season) {
            if (search.via.nlc && [search.origin.nlc, search.destination.nlc].includes(search.via.nlc)) {
                errors = errors.set('viaAvoid', 'booking.journey.errors.viaSameStation');
            }
            if (search.avoid.nlc && [search.origin.nlc, search.destination.nlc].includes(search.avoid.nlc)) {
                errors = errors.set('viaAvoid', 'booking.journey.errors.avoidSameStation');
            }
        }

        return errors;
    }

    _handleStationChange = (field, value) => {
        this.props.onChange(this.props.search.set(field, value));
        this.setState({ stationFieldsTouched: true });
    };

    _handleSwapStations = () => {
        const { search, season } = this.props;
        let types = StationTypes;
        if (search.smartcard) {
            types = SmartcardStationTypes;
        } else if (season) {
            types = SeasonStationTypes;
        }

        TrackingActions.widgetStationSwitch();

        const destination = search.getStationType(search.destination, types.ORIGIN);
        const origin = search.getStationType(search.origin, types.DESTINATION);
        const swappedSearch = search
            .set('origin', destination)
            .set('destination', origin);
        this.props.onChange(swappedSearch);
    };

    _renderStation(name, type) {
        const { errors } = this.state;
        const labelText = t(`booking.journey.${name}.label`);
        const placeholder = t(`booking.journey.${name}`);

        const station = this.props.search[name];
        const classes = classnames({
            'journey__station': true,
            'journey__station--selected': station.name
        });

        const error = errors.get(name);

        const label = (
            <span className="sr-only">
                {error ? `${t('error.title')}: ${t(error)}` : labelText}.
                {t('booking.journey.mandatoryField')}
            </span>
        );

        const inputClassName = classnames({ 'station--error': error });

        const focused = name === 'destination'
            ? errors.get('origin') ? false : error
            : error;


        return (
            <div className={classes}>
                <StationSelector
                    name={name}
                    station={station}
                    placeholder={placeholder}
                    onChange={this._handleStationChange.bind(this, name)}
                    stationType={type}
                    label={label}
                    title={labelText}
                    className={inputClassName}
                    overlayResults={this.props.overlayResults}
                    focused={focused}
                    isInvalid={!!error}
                    errorMessage={error ? t(error) : null}
                    required
                />
            </div>
        );
    }

    _getStationFinderModalProps() {
        return {
            show: this.state.showStationFinderModal,
            className: 'grid-modal modal--sm stationFinder__container-modal',
            onClose: this._hideStationFinderModal,
            hideContinue: true,
            titleText: t('stationFinder.modal.title'),
            closeText: 'button.text.cancel',
            continueText: 'button.text.continue'
        };
    }

    _showStationFinderModal = () => {
        if (window.innerWidth <= BreakPoints.TABLET_PORTRAIT_WIDTH) {
            this.setState({
                showStationFinderModal: true
            });
        } else {
            StationFinderActions.setExpanded(!StationFinderStore.get('isExpanded'));
        }
    }

    _hideStationFinderModal = () => {
        this.setState({
            showStationFinderModal: false
        });
    }

    render() {
        const { search, season, promoCode, onChange, enableVia } = this.props;
        const { errors } = this.state;
        let types = StationTypes;

        if (search.smartcard && season) {
            types = SmartcardSeasonStationTypes;
        } else if (search.smartcard) {
            types = SmartcardStationTypes;
        } else if (season) {
            types = SeasonStationTypes;
        }

        const showVia = FeatureFlagStore.get('enableViaAvoid') && enableVia && !promoCode && !season;

        let viaAvoidLabel;
        const viaAvoidError = errors.get('viaAvoid');
        if (viaAvoidError) { viaAvoidLabel = <span className="sr-only">. {t('error.title')}: {t(viaAvoidError)}</span>; }

        return (
            <section className="booking__section">
                <SectionErrors errors={errors.toSet().toList().toArray()} />

                <div className="margin-bottom--sm box box--rounded">
                    <div className="journey">
                        {this._renderStation('origin', types.ORIGIN)}

                        <div className="journey__swap">
                            <button className="svg-switch" onClick={this._handleSwapStations} >
                                <Icon icon="switch" />
                                <span className="sr-only">{t('booking.journey.switch')}</span>
                            </button>
                        </div>

                        {this._renderStation('destination', types.DESTINATION)}
                    </div>
                </div>

                <ResponsiveRender maxWidth={BreakPoints.TABLET_PORTRAIT_WIDTH} >
                    <BaseModal {...this._getStationFinderModalProps()} >
                        <StationFinder
                            closeContainerModal={this._hideStationFinderModal}
                            currentSearch={search}
                            className="mid-section"
                            isMobile
                            onChange={this._handleStationChange}
                            origin={search.origin}
                            destination={search.destination} />
                    </BaseModal>
                </ResponsiveRender>

                <div className="stationFinder">
                    {showVia && <ViaAvoid search={search}
                        onChange={onChange}
                        season={season}
                        errorLabel={viaAvoidLabel} />}

                    {this.props.enableStationFinder &&
                        <div className="stationFinder__cta-group">
                            <StationFinderToggle
                                onActivate={this._showStationFinderModal} />
                            <Tutorial name={Tutorials.STATION_FINDER} notAccessible>
                                <Icon icon="info" />
                            </Tutorial>
                        </div>}
                </div>
            </section>
        );
    }
}

export const unwrapped = JourneySelectorOpen;

export default connectToStores([PromoCodeStore], () => ({ promoCode: PromoCodeStore.getActive() }))(JourneySelectorOpen);



// WEBPACK FOOTER //
// ./src/components/search/JourneySelectorOpen.js